import { useState } from "react";
import { devNotesAssetUpload, devNotesUpdate, fileToBase64 } from "@/lib/devActionableNotesClient";
import type { DevActionableNote } from "./devActionableNoteTypes";
import { notePlacement } from "./devActionableNoteTypes";
import {
  DevPanelActionableNoteBodyPreview,
  DevPanelActionableNoteHeader,
  DevPanelActionableNoteMeta,
} from "./DevPanelActionableNoteCard";

type Props = {
  notes: DevActionableNote[];
  getToken: () => Promise<string | null>;
  savingId: string | null;
  setSavingId: (id: string | null) => void;
  onError: (msg: string) => void;
  onDone: () => void;
};

export function DevPanelActionableNotesPendingEdit({
  notes,
  getToken,
  savingId,
  setSavingId,
  onError,
  onDone,
}: Props) {
  const [editId, setEditId] = useState<string | null>(null);
  const [editBody, setEditBody] = useState<Record<string, string>>({});
  const [repoPath, setRepoPath] = useState<Record<string, string>>({});

  async function saveEdit(note: DevActionableNote) {
    const token = await getToken();
    if (!token) return;
    const body = (editBody[note.id] ?? note.body).trim();
    if (!body) return;
    setSavingId(note.id);
    const path = (repoPath[note.id] ?? notePlacement(note)?.repo_path ?? "").trim();
    const placement = path ? { repo_path: path } : undefined;
    const result = await devNotesUpdate(token, { id: note.id, body, placement });
    setSavingId(null);
    if (!result.ok) {
      onError(result.error);
      return;
    }
    setEditId(null);
    onDone();
  }

  async function uploadAsset(note: DevActionableNote, file: File) {
    const token = await getToken();
    if (!token) return;
    setSavingId(note.id);
    try {
      const content_base64 = await fileToBase64(file);
      const path = (repoPath[note.id] ?? notePlacement(note)?.repo_path ?? "").trim();
      const result = await devNotesAssetUpload(token, {
        id: note.id,
        content_base64,
        content_type: file.type || "image/svg+xml",
        placement: path ? { repo_path: path } : undefined,
      });
      if (!result.ok) onError(result.error);
      else onDone();
    } catch (e) {
      onError(e instanceof Error ? e.message : "Upload failed");
    }
    setSavingId(null);
  }

  if (!notes.length) return null;

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold text-violet-950">My pending submissions</h3>
      <ul className="space-y-3">
        {notes.map((note) => (
          <li key={note.id} className="rounded-xl border border-violet-200/80 bg-white p-3 space-y-2 dark:bg-zinc-900">
            <DevPanelActionableNoteHeader note={note} />
            {editId === note.id ? (
              <>
                <textarea
                  className="w-full rounded border border-violet-200 px-2 py-1.5 text-sm"
                  rows={4}
                  value={editBody[note.id] ?? note.body}
                  onChange={(e) => setEditBody((p) => ({ ...p, [note.id]: e.target.value }))}
                />
                <label className="block text-[11px] text-gray-600">
                  Target path (public/… or src/assets/…)
                  <input
                    className="mt-0.5 w-full rounded border border-gray-200 px-2 py-1 text-xs font-mono"
                    value={repoPath[note.id] ?? notePlacement(note)?.repo_path ?? ""}
                    onChange={(e) => setRepoPath((p) => ({ ...p, [note.id]: e.target.value }))}
                    placeholder="public/ui/example.svg"
                  />
                </label>
                <label className="block text-[11px] text-gray-600">
                  Replace graphic
                  <input
                    type="file"
                    accept=".svg,.png,.webp,.jpg,.jpeg,image/svg+xml,image/png,image/webp,image/jpeg"
                    className="mt-0.5 block w-full text-xs"
                    disabled={savingId === note.id}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) void uploadAsset(note, f);
                      e.target.value = "";
                    }}
                  />
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    className="rounded-lg bg-violet-700 px-3 py-1 text-xs font-semibold text-white disabled:opacity-50"
                    disabled={savingId === note.id}
                    onClick={() => void saveEdit(note)}
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    className="rounded-lg border border-gray-300 px-3 py-1 text-xs"
                    onClick={() => setEditId(null)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <DevPanelActionableNoteBodyPreview note={note} />
                <DevPanelActionableNoteMeta note={note} />
                <button
                  type="button"
                  className="rounded-lg border border-violet-300 px-3 py-1 text-xs font-medium text-violet-900"
                  onClick={() => {
                    setEditId(note.id);
                    setEditBody((p) => ({ ...p, [note.id]: note.body }));
                    setRepoPath((p) => ({
                      ...p,
                      [note.id]: p[note.id] ?? notePlacement(note)?.repo_path ?? "",
                    }));
                  }}
                >
                  Edit
                </button>
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
